import type { ReactNode } from 'react'
import clsx from 'clsx'

export type BadgeTone = 'neutral' | 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'violet'

// Barvy textu volené tak, aby na světlém podkladu splnily WCAG AA (≥ 4,5 : 1) i při 12 px.
const TONES: Record<BadgeTone, { badge: string; dot: string }> = {
  neutral: { badge: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-400' },
  brand: { badge: 'bg-brand-50 text-brand-700 ring-brand-200', dot: 'bg-brand-500' },
  info: { badge: 'bg-sky-50 text-sky-800 ring-sky-200', dot: 'bg-sky-500' },
  success: { badge: 'bg-emerald-50 text-emerald-800 ring-emerald-200', dot: 'bg-emerald-500' },
  warning: { badge: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  danger: { badge: 'bg-red-50 text-red-700 ring-red-200', dot: 'bg-red-500' },
  violet: { badge: 'bg-violet-50 text-violet-800 ring-violet-200', dot: 'bg-violet-500' },
}

/** Štítek (stav, typ, počet). Písmo 12 px — nikdy menší. */
export function Badge({
  children,
  tone = 'neutral',
  dot = false,
  icon,
  className,
}: {
  children: ReactNode
  tone?: BadgeTone
  dot?: boolean
  icon?: ReactNode
  className?: string
}) {
  const t = TONES[tone]
  return (
    <span className={clsx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', t.badge, className)}>
      {dot && <span className={clsx('h-1.5 w-1.5 rounded-full', t.dot)} aria-hidden />}
      {icon}
      {children}
    </span>
  )
}

/**
 * Stavový štítek: mapa „kód stavu → popisek + tón". Neznámý kód se ukáže tak, jak je (neutrálně).
 * Stránky si mapu předávají samy (stavy příprav, nepřítomností, úkolů…).
 */
export function StatusBadge<K extends string>({
  status,
  map,
  dot = true,
}: {
  status: K | string
  map: Partial<Record<string, { label: string; tone: BadgeTone }>>
  dot?: boolean
}) {
  const conf = map[status] ?? { label: status, tone: 'neutral' as BadgeTone }
  return <Badge tone={conf.tone} dot={dot}>{conf.label}</Badge>
}

/** Počet v kulatém odznaku (menu, záložky). */
export function CountBadge({ count, tone = 'neutral', className }: { count: number; tone?: 'neutral' | 'brand'; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
        tone === 'brand' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-ink-700',
        className,
      )}
    >
      {count}
    </span>
  )
}
